import {
  isKnownPaymentMethod,
  normalizePaymentMethodKey,
  type KnownPaymentMethod,
} from '@/lib/reporting/paymentMethods';

/**
 * Methods shown in the payment window.
 * Cash, card, bank transfer, and other mean the money is already received.
 * MobilePay stays in the list, but it starts a Clinio Pay request. It is not
 * written to invoice_payments until the provider webhook confirms it.
 */
export const MANUAL_PAYMENT_METHODS = [
  'cash',
  'card',
  'bank_transfer',
  'mobilepay',
  'other',
] as const satisfies readonly KnownPaymentMethod[];

export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export function isManualPaymentMethod(value: string): value is ManualPaymentMethod {
  return (MANUAL_PAYMENT_METHODS as readonly string[]).includes(value);
}

/** MobilePay in the staff payment window requests payment. It does not record it. */
export function isPaymentRequestMethod(method: string): method is 'mobilepay' {
  return method === 'mobilepay';
}

export function paymentMethodLabelKey(method: KnownPaymentMethod): string {
  switch (method) {
    case 'cash':
      return 'invoices.payment_methods.cash';
    case 'card':
      return 'invoices.payment_methods.card';
    case 'bank_transfer':
      return 'invoices.payment_methods.bank_transfer';
    case 'mobilepay':
      return 'invoices.payment_methods.mobilepay';
    case 'clinio_pay':
      return 'invoices.payment_methods.clinio_pay';
    case 'invoice':
      return 'invoices.payment_methods.invoice';
    case 'other':
      return 'invoices.payment_methods.other';
    default: {
      const _exhaustive: never = method;
      return _exhaustive;
    }
  }
}

/**
 * Value written to invoice_payments.payment_method.
 * Known methods stay canonical. "Other" stores the practitioner's description.
 */
export function recordedPaymentMethod(method: string, otherDetail: string): string | null {
  if (!isManualPaymentMethod(method) || isPaymentRequestMethod(method)) return null;
  if (method !== 'other') return method;
  const detail = otherDetail.trim();
  return detail.length > 0 ? detail : null;
}

export function storedPaymentMethodLabel(
  raw: string | null | undefined,
  translate: (key: string) => string,
): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return '—';
  const key = normalizePaymentMethodKey(trimmed);
  if (key !== 'inherit' && isKnownPaymentMethod(key)) {
    return translate(paymentMethodLabelKey(key));
  }
  return trimmed;
}

export function todayDateInputValue(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isPaymentDateOnOrBeforeToday(date: string, now = new Date()): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= todayDateInputValue(now);
}

export type ManualPaymentIssue =
  | 'method'
  | 'other'
  | 'amount_required'
  | 'amount_invalid'
  | 'date_required'
  | 'date_future';

/** Danish amount for the payment field: 93750 → "937,50". */
export function formatPaymentAmountInput(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(Math.round(cents));
  return `${sign}${Math.trunc(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

/** Accepts "937,50" and "937.50". Returns øre, or null when the text is not a number. */
export function parsePaymentAmountCents(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '');
  if (!trimmed) return null;
  const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

/** Field problems for a staff payment. MobilePay is a request, not a recorded payment. */
export function manualPaymentIssues(input: {
  method: string;
  otherDetail: string;
  amount: string;
  paymentDate: string;
  remainingCents: number;
  now?: Date;
}): ManualPaymentIssue[] {
  if (isPaymentRequestMethod(input.method)) return [];
  const issues: ManualPaymentIssue[] = [];
  if (!recordedPaymentMethod(input.method, input.otherDetail)) {
    issues.push(input.method === 'other' ? 'other' : 'method');
  }
  const amountCents = parsePaymentAmountCents(input.amount);
  if (!input.amount.trim()) issues.push('amount_required');
  else if (amountCents == null || amountCents <= 0 || amountCents > input.remainingCents) {
    issues.push('amount_invalid');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.paymentDate)) issues.push('date_required');
  else if (!isPaymentDateOnOrBeforeToday(input.paymentDate, input.now)) issues.push('date_future');
  return issues;
}

/**
 * A payment on today's calendar day keeps the real clock.
 * A backdated day has no clock in the date field, so it stays at local noon
 * and the calendar day is preserved.
 */
export function paymentDateInputToIso(date: string, now = new Date()): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return now.toISOString();
  if (date === todayDateInputValue(now)) return now.toISOString();
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day, 12, 0, 0, 0).toISOString();
}
